import {
  getFinancialAssetRecords,
  getAssetSeqNum,
  getReoSeqNum,
  getReoAssetLabel,
  getResidenceAssetLabelFor
} from "./asset";
import { getLiabilityRecords, getLiabilitySeqNum } from "./liability";
import { income_export_components } from "../../calculations/income";
import { getOwners } from "../../constants/debt_owners";
import { parsePropertyKey } from "../../constants/property_list";
import { MARRIED } from "../../constants/marital_status";

export function getBorrowerRoleSeqNum(borrowerIndex) {
  return (borrowerIndex + 1) * 10 + 1;
}

export function populateRelationships(builder, { document }) {
  const borrowers = document.applicant.borrowers;
  const allAssets = getFinancialAssetRecords(document);
  const allLiabilities = getLiabilityRecords(document);
  const reoProperties = document.investment_properties || [];
  let relSeq = 1;

  // Assets are owned per borrower, not jointly — a co-borrower can hold their
  // own account. getFinancialAssetRecords already tags each record with the
  // borrower it came from; this used to be hard-coded to BORROWER_11, which
  // flattened every co-borrower account onto Borrower 1 on the way out.
  allAssets.forEach((reserve, globalAssetIndex) => {
    const seqNum = getAssetSeqNum(globalAssetIndex);
    const ownerIndex = reserve.borrowerIndex || 0;
    const rel = builder.ele("RELATIONSHIP");
    rel.att("SequenceNumber", String(relSeq++));
    rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/ASSET_IsAssociatedWith_ROLE");
    rel.att("xlink:from", `ASSET_${seqNum}`);
    rel.att("xlink:to", `BORROWER_${getBorrowerRoleSeqNum(ownerIndex)}`);
  });

  reoProperties.forEach((ip, reoIndex) => {
    const seqNum = getReoSeqNum(reoIndex);
    const owners = getOwners(ip);
    const ownerList = owners.length > 0 ? owners : ["0"];
    ownerList.forEach(ownerIndexStr => {
      const ownerIndex = parseInt(ownerIndexStr, 10) || 0;
      const rel = builder.ele("RELATIONSHIP");
      rel.att("SequenceNumber", String(relSeq++));
      rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/ASSET_IsAssociatedWith_ROLE");
      rel.att("xlink:from", `ASSET_OWNED_${seqNum}`);
      rel.att("xlink:to", `BORROWER_${getBorrowerRoleSeqNum(ownerIndex)}`);
    });
  });

  // Each debt is written once (same order as populateLiabilities, so the
  // labels line up) and gets ONE arc per owner. A joint debt therefore emits
  // two (or more) arcs to the same LIABILITY — the DU-correct shape.
  allLiabilities.forEach((liability, liabilityIndex) => {
    const seqNum = getLiabilitySeqNum(liabilityIndex);
    const owners = getOwners(liability);
    const ownerList = owners.length > 0 ? owners : ["0"];
    ownerList.forEach((ownerIndexStr) => {
      const ownerIndex = parseInt(ownerIndexStr, 10) || 0;
      const rel = builder.ele("RELATIONSHIP");
      rel.att("SequenceNumber", String(relSeq++));
      rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/LIABILITY_IsAssociatedWith_ROLE");
      rel.att("xlink:from", `LIABILITY_${seqNum}`);
      rel.att("xlink:to", `BORROWER_${getBorrowerRoleSeqNum(ownerIndex)}`);
    });
  });

  // Link each mortgage/lien to the property (or properties) it is secured by,
  // so DU knows which loan is on which property — including the one being paid
  // off. A blanket mortgage can carry several arcs.
  allLiabilities.forEach((liability, liabilityIndex) => {
    const attached = Array.isArray(liability.attached_properties)
      ? liability.attached_properties
      : [];
    if (attached.length === 0) return;
    const seqNum = getLiabilitySeqNum(liabilityIndex);
    attached.forEach(key => {
      const parsed = parsePropertyKey(key);
      if (!parsed) return;
      const assetLabel =
        parsed.type === "reo"
          ? getReoAssetLabel(parsed.index)
          : getResidenceAssetLabelFor(document, parsed.index);
      const rel = builder.ele("RELATIONSHIP");
      rel.att("SequenceNumber", String(relSeq++));
      rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/ASSET_IsAssociatedWith_LIABILITY");
      rel.att("xlink:from", assetLabel);
      rel.att("xlink:to", `LIABILITY_${seqNum}`);
    });
  });

  // Tell DU the borrowers pulled a joint credit report. This arc is what POINT
  // uses to PAIR borrowers into applications (Borrower + Co-Borrower). It must
  // follow the actual marriage/pairing, NOT chain every co-borrower to the
  // primary — otherwise POINT tangles a 2-couple file (e.g. Testcase+Smith and
  // Jones+Jones) into one group and drops all but the first borrower. Each
  // co-borrower links to their spouse when the file pairs them (one arc per
  // couple), otherwise falls back to the primary borrower.
  if (borrowers.length > 1) {
    for (let i = 1; i < borrowers.length; i++) {
      const borrower = borrowers[i];
      let partner = 0;
      if (borrower && borrower.marital_status === MARRIED) {
        const m = parseInt(borrower.married_to, 10);
        if (Number.isInteger(m) && m >= 0 && m < borrowers.length && m !== i) {
          partner = m;
        }
      }
      // For a spouse pair, emit the arc once — from the higher index to the
      // lower — so we don't also write the reciprocal. Fallback-to-primary arcs
      // (partner === 0) are always emitted (co-borrower -> primary).
      if (partner !== 0 && partner > i) {
        continue;
      }
      const rel = builder.ele("RELATIONSHIP");
      rel.att("SequenceNumber", String(relSeq++));
      rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/ROLE_SharesJointCreditReportWith_ROLE");
      rel.att("xlink:from", `BORROWER_${getBorrowerRoleSeqNum(i)}`);
      rel.att("xlink:to", `BORROWER_${getBorrowerRoleSeqNum(partner)}`);
    }
  }

  borrowers.forEach((borrower, borrowerIndex) => {
    (borrower.income.sources || []).forEach((incomeSource, sourceIndex) => {
      // DSCR rental income has no employer — skip the income->employer arc so it
      // never dangles to a non-existent EMPLOYER label.
      if (incomeSource && incomeSource.calculator === "NonQM DSCR") {
        return;
      }
      const employerSeq = (borrowerIndex + 1) * 100 + (sourceIndex + 1) * 10 + 1;
      // One arc per exported component item (Base + non-zero variables). The
      // sequence formula MUST match the CURRENT_INCOME_ITEM @forEach in
      // mismo_config so each item links to its employer.
      income_export_components(incomeSource).forEach(comp => {
        const incomeItemSeq =
          (borrowerIndex + 1) * 1000 + (sourceIndex + 1) * 10 + 101 + comp.offset;
        const rel = builder.ele("RELATIONSHIP");
        rel.att("SequenceNumber", String(relSeq++));
        rel.att("xlink:arcrole", "urn:fdc:mismo.org:2009:residential/CURRENT_INCOME_ITEM_IsAssociatedWith_EMPLOYER");
        rel.att("xlink:from", `CURRENT_INCOME_ITEM_${incomeItemSeq}`);
        rel.att("xlink:to", `EMPLOYER_${employerSeq}`);
      });
    });
  });
}
